// Web Push notification helper — wraps the browser PushManager + our backend.
// Safe to import on any page; functions short-circuit gracefully when push
// isn't supported (older browsers, iOS Safari before PWA install, etc).

import api from '../services/api';

const STORAGE_PROMPTED = 'jd_push_prompted_at';

/** Convert a URL-safe base64 string into a Uint8Array for `applicationServerKey`. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** True if app is running as an installed PWA (relevant for iOS). */
export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/** Best-effort attempt to fetch the current SW push subscription, if any. */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Has the user already opted in (and is the browser still happy)? */
export async function isPushEnabled() {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  const sub = await getCurrentSubscription();
  return !!sub;
}

/** Save a "we already asked" flag so we don't pester them on every page load. */
export function markPromptShown() {
  try { localStorage.setItem(STORAGE_PROMPTED, String(Date.now())); } catch { /* ignore */ }
}

export function hasBeenPrompted() {
  try { return !!localStorage.getItem(STORAGE_PROMPTED); } catch { return false; }
}

/** Subscribe the user to Web Push. Returns true on success. */
export async function subscribePush() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    // 1. Request permission if not already granted/denied
    let perm = Notification.permission;
    if (perm === 'default') perm = await Notification.requestPermission();
    if (perm !== 'granted') return false;

    // 2. Fetch VAPID public key from server
    const { data } = await api.get('/push/public-key');
    if (!data?.public_key) return false;

    // 3. Subscribe (or reuse existing)
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }

    // 4. Persist to backend
    const json = sub.toJSON();
    await api.post('/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys || {},
    });
    return true;
  } catch (e) {
    console.warn('[push] subscribe failed', e);
    return false;
  }
}

/** Unsubscribe locally + on the server. Returns true if cleared. */
export async function unsubscribePush() {
  if (!isPushSupported()) return false;
  try {
    const sub = await getCurrentSubscription();
    if (!sub) return true;
    const json = sub.toJSON();
    try {
      await api.post('/push/unsubscribe', { endpoint: json.endpoint, keys: json.keys || {} });
    } catch { /* server cleanup best-effort */ }
    await sub.unsubscribe();
    return true;
  } catch (e) {
    console.warn('[push] unsubscribe failed', e);
    return false;
  }
}
